// AAMVA PDF417 driver's license parser — works for all 50 US states,
// DC, US territories, and Canadian provinces (AAMVA is a federal spec).
//
// Input: raw string emitted by a 2D scanner reading the PDF417 barcode
//        on the back of a DL/ID. Header looks like:
//          "@\n\u001e\rANSI 636014040002DL00410288..."
// Output: parsed object, or null if the input is not a valid AAMVA blob.

// Whitelist of official AAMVA DL/ID data element codes (versions 2-10) plus
// the generic pattern for jurisdiction-specific codes (always start with Z).
// Used to locate field boundaries by matching known codes directly, instead
// of relying on separators — some scanners drop or garble the AAMVA LF/RS/CR
// separators between fields entirely (confirmed on Motorola/Zebra DS9808),
// so anchoring on separators alone is unreliable.
const KNOWN_CODES = [
  "DAA", "DAB", "DAC", "DAD", "DAE", "DAF", "DAG", "DAH", "DAI", "DAJ", "DAK",
  "DAL", "DAM", "DAN", "DAO", "DAP", "DAQ", "DAR", "DAS", "DAT", "DAU", "DAV",
  "DAW", "DAX", "DAY", "DAZ", "DBA", "DBB", "DBC", "DBD", "DBE", "DBF", "DBG",
  "DBH", "DBI", "DBJ", "DBK", "DBL", "DBM", "DBN", "DBO", "DBP", "DBQ", "DBR",
  "DBS", "DCA", "DCB", "DCD", "DCE", "DCF", "DCG", "DCH", "DCI", "DCJ", "DCK",
  "DCL", "DCM", "DCN", "DCO", "DCP", "DCQ", "DCR", "DCS", "DCT", "DCU", "DDA",
  "DDB", "DDC", "DDD", "DDE", "DDF", "DDG", "DDH", "DDI", "DDJ", "DDK", "DDL",
];
const CODE_RE = new RegExp(`(?:${KNOWN_CODES.join("|")}|Z[A-Z]{2})`, "g");

export function parseAAMVA(raw) {
  if (!raw || typeof raw !== "string") return null;
  // Must contain the ANSI header — otherwise it's a UPC or something else
  if (!/ANSI\s?\d{6}/.test(raw) && !raw.includes("@\n") && !raw.includes("DAQ")) {
    return null;
  }
  // Strip control characters entirely (separators, if present, carry no
  // value — field boundaries are found via the known-code positions below).
  const cleaned = raw.replace(/[\x00-\x1f]/g, "");

  // The AAMVA header's subfile-type marker ("DL"/"ID") sits directly against
  // the first field code with NO separator (e.g. "...IDDAQ34500966..."),
  // which can accidentally overlap a real code (here "DDA" inside "IDDAQ").
  // Use the header's own declared entry count to skip past the header +
  // subfile-type marker precisely, so field matching starts clean.
  let searchText = cleaned;
  const headerMatch = cleaned.match(/ANSI\s?\d{6}(\d{2})(\d{2})(\d{2})/);
  if (headerMatch) {
    const numEntries = parseInt(headerMatch[3], 10) || 0;
    let pos = headerMatch.index + headerMatch[0].length;
    let subfileType = null;
    for (let i = 0; i < numEntries && pos + 10 <= cleaned.length; i++) {
      if (i === 0) subfileType = cleaned.slice(pos, pos + 2);
      pos += 10;
    }
    if (subfileType && cleaned.slice(pos, pos + 2) === subfileType) pos += 2;
    if (pos > headerMatch.index) searchText = cleaned.slice(pos);
  }

  const fields = {};
  const matches = [...searchText.matchAll(CODE_RE)];
  for (let i = 0; i < matches.length; i++) {
    const code = matches[i][0];
    const valStart = matches[i].index + code.length;
    const valEnd = i + 1 < matches.length ? matches[i + 1].index : searchText.length;
    // First occurrence of a code wins — protects real subfile data from
    // being overwritten by an accidental match inside a trailing
    // jurisdiction-specific/security block.
    if (!(code in fields)) fields[code] = searchText.slice(valStart, valEnd).trim();
  }
  // If we found nothing, this wasn't AAMVA
  if (Object.keys(fields).length < 3) return null;

  const first = fields.DAC || fields.DCT || "";
  const last = fields.DCS || fields.DAB || "";
  const middle = fields.DAD || "";
  const dobRaw = fields.DBB || "";       // MMDDYYYY
  const expRaw = fields.DBA || "";       // MMDDYYYY
  const state = fields.DAJ || "";
  const licenseNo = fields.DAQ || "";

  const parseAAMVADate = (s) => {
    if (!s || s.length !== 8) return null;
    const mm = parseInt(s.slice(0, 2), 10);
    const dd = parseInt(s.slice(2, 4), 10);
    const yyyy = parseInt(s.slice(4, 8), 10);
    if (!mm || !dd || !yyyy) return null;
    return new Date(yyyy, mm - 1, dd);
  };

  const dob = parseAAMVADate(dobRaw);
  const expiration = parseAAMVADate(expRaw);
  if (!dob) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;

  return {
    first_name: first,
    middle_name: middle,
    last_name: last,
    full_name: [first, middle, last].filter(Boolean).join(" "),
    dob,
    expiration,
    is_expired: expiration ? expiration < today : false,
    state,
    license_no: licenseNo,
    age,
  };
}

// Parse MMDDYY manual entry — cashier types 6 digits, we assume 20YY if YY <= today's year+1,
// otherwise 19YY.
export function parseManualDOB(mmddyy) {
  if (!/^\d{6}$/.test(String(mmddyy).trim())) return null;
  const s = String(mmddyy).trim();
  const mm = parseInt(s.slice(0, 2), 10);
  const dd = parseInt(s.slice(2, 4), 10);
  const yy = parseInt(s.slice(4, 6), 10);
  if (!mm || mm > 12 || !dd || dd > 31) return null;
  const today = new Date();
  const currentYY = today.getFullYear() % 100;
  const yyyy = yy <= currentYY + 1 ? 2000 + yy : 1900 + yy;
  const dob = new Date(yyyy, mm - 1, dd);
  today.setHours(0, 0, 0, 0);
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return { dob, age };
}
